import {TabContext, TabList, TabPanel} from '@mui/lab';
import {Badge, Box, Tab, useMediaQuery, useTheme} from '@mui/material';
import React from 'react';
import {useNavigate} from 'react-router';

type TabElementProps = {
  value: string;
  label: string;
  count?: number;
  icon?: React.ReactElement;
  children?: React.ReactNode;
};

const ShowTabs = (props: ShowTabsProps) => {
  const {baseUrl, value: initialValue, defaultValue = '0', children} = props;

  const [value, setValue] = React.useState<string>(initialValue || defaultValue);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    if (initialValue) setValue(initialValue);
  }, [initialValue]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (baseUrl) {
      const suffix = newValue !== defaultValue ? '/' + newValue : '';
      navigate(`${baseUrl}${suffix}`);
    }
    setValue(newValue);
  };

  return (
    <TabContext value={value}>
      <Box sx={{borderBottom: 1, borderColor: 'divider', mx: {xs: -1.5, sm: 0}}}>
        <TabList
          onChange={handleChange}
          aria-label="page tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: {xs: 44, sm: 48},
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: {xs: 44, sm: 48},
              minWidth: 'auto',
              px: {xs: 1.25, sm: 2},
              fontWeight: 600,
              fontSize: {xs: 13, sm: 14},
              color: 'text.secondary'
            },
            '& .Mui-selected': {color: 'primary.main'},
            '& .MuiTabs-indicator': {height: 3, borderRadius: 2}
          }}
        >
          {React.Children.map(children, (field) => {
            const element = field as React.ReactElement<TabElementProps>;
            const tabValue = element.props.value;
            const label = element.props.label;
            const count = element.props.count;
            const icon = element.props.icon;
            return (
              <Tab
                key={tabValue}
                icon={isMobile ? undefined : icon}
                iconPosition="start"
                label={
                  <Badge badgeContent={count} color="primary" invisible={!count || count === 0}>
                    {label}
                  </Badge>
                }
                value={tabValue}
              />
            );
          })}
        </TabList>
      </Box>
      {React.Children.map(children, (field) => {
        const element = field as React.ReactElement<TabElementProps>;
        const tabValue = element.props.value;
        return (
          <TabPanel key={tabValue} value={tabValue} sx={{padding: {xs: '16px 0 0', sm: '24px 0 0'}}}>
            {element}
          </TabPanel>
        );
      })}
    </TabContext>
  );
};

export default ShowTabs;

type ShowTabsProps = {
  baseUrl?: string;
  value?: string;
  defaultValue?: string;
  children: Array<React.ReactElement> | React.ReactElement;
};
